import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { api } from '../api'
import Why from '../components/Why'
import TournamentEditor from './TournamentEditor'
import { t } from '../i18n'
import { confirmAction, haptic, notify, openFile } from '../telegram'
import { isPairsKind, isRoundByRound } from '../types'
import type { Game, PlayerMe, ReportLinks, TMatch, Tournament, TPlayer } from '../types'

interface Props {
  game: Game
  me: PlayerMe
  /** Старт и завершение меняют статус игры — экран игры перечитывает её */
  onGameChanged: () => void
}

function fmtDelta(d: number): string {
  const sign = d > 0 ? '+' : d < 0 ? '−' : '±'
  return `${sign}${Math.abs(d).toFixed(2)}`
}

function names(players: TPlayer[], meId: number): string {
  return players.map((p) => (p.id === meId ? `${p.first_name} (${t().yourMatch})` : p.first_name)).join(' + ')
}

const MEDAL: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' }

/** Строка матча. Организатор нажимает — открывается ввод счёта. */
function MatchLine({
  match,
  meId,
  editable,
  total,
  busy,
  correcting = false,
  king = false,
  moves,
  onSave,
}: {
  match: TMatch
  meId: number
  editable: boolean
  /** «Король корта»: при равном счёте — выбор золотого очка, корт 1 с короной */
  king?: boolean
  /** «Король корта»: куда пары уходят после раунда (▲ / ▼ / 👑 / ☕ / =) */
  moves?: { a: string; b: string }
  /** Исправление счёта завершённого турнира: без «Стереть», с подтверждением */
  correcting?: boolean
  /** Счёт очками: очки матча в сумме дают total — второе поле считаем сами */
  total: number | null
  busy: boolean
  onSave: (a: number | null, b: number | null, golden: 'a' | 'b' | null) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [a, setA] = useState(match.score_a?.toString() ?? '')
  const [b, setB] = useState(match.score_b?.toString() ?? '')
  const [golden, setGolden] = useState<'a' | 'b' | null>(match.golden)
  const scored = match.score_a != null && match.score_b != null
  const mine = [...match.a, ...match.b].some((p) => p.id === meId)
  const tie = scored && match.score_a === match.score_b
  const aWon = scored && (match.score_a! > match.score_b! || (tie && match.golden === 'a'))
  const bWon = scored && (match.score_b! > match.score_a! || (tie && match.golden === 'b'))
  const tieTyped = king && a !== '' && a === b

  const digits = (v: string) => v.replace(/\D/g, '').slice(0, 2)
  const rest = (v: string) => (total && v !== '' && Number(v) <= total ? String(total - Number(v)) : null)

  function typeA(raw: string) {
    const v = digits(raw)
    setA(v)
    const other = rest(v)
    if (other != null) setB(other)
  }

  function typeB(raw: string) {
    const v = digits(raw)
    setB(v)
    const other = rest(v)
    if (other != null) setA(other)
  }

  async function save(clear = false) {
    if (clear) {
      await onSave(null, null, null)
      setA('')
      setB('')
      setGolden(null)
    } else {
      if (a === '' || b === '') return notify(t().bothScores)
      if (tieTyped && !golden) return notify(t().goldenAsk)
      if (correcting && !(await confirmAction(t().correctConfirm))) return
      await onSave(Number(a), Number(b), tieTyped ? golden : null)
    }
    setOpen(false)
  }

  return (
    <div className={`t-match${mine ? ' mine' : ''}${king && match.court === 1 ? ' crown' : ''}`}>
      <button
        className="t-match-head"
        disabled={!editable}
        onClick={() => editable && setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="muted small t-court">{t().courtN(match.court)}</span>
        <span className="t-sides">
          <span style={{ fontWeight: aWon ? 700 : 400 }}>
            {moves?.a && <span className="k-move">{moves.a}</span>}
            {match.team_a ? `${match.team_a}. ` : ''}
            {names(match.a, meId)}
          </span>
          <span style={{ fontWeight: bWon ? 700 : 400 }}>
            {moves?.b && <span className="k-move">{moves.b}</span>}
            {match.team_b ? `${match.team_b}. ` : ''}
            {names(match.b, meId)}
          </span>
        </span>
        <span className="t-score">
          {scored ? (
            <>
              <span style={{ fontWeight: aWon ? 700 : 400 }}>
                {match.score_a}
                {tie && match.golden === 'a' ? '★' : ''}
              </span>
              <span style={{ fontWeight: bWon ? 700 : 400 }}>
                {match.score_b}
                {tie && match.golden === 'b' ? '★' : ''}
              </span>
            </>
          ) : (
            <span className="muted">—</span>
          )}
        </span>
      </button>

      {open && (
        <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <input
            inputMode="numeric"
            className="t-input"
            aria-label={names(match.a, meId)}
            value={a}
            onChange={(e) => typeA(e.target.value)}
            autoFocus
          />
          <span className="muted">:</span>
          <input
            inputMode="numeric"
            className="t-input"
            aria-label={names(match.b, meId)}
            value={b}
            onChange={(e) => typeB(e.target.value)}
          />
          <button disabled={busy} onClick={() => save()}>
            {t().saveScore}
          </button>
          {scored && !correcting && (
            <button className="ghost" disabled={busy} onClick={() => save(true)}>
              {t().clearScore}
            </button>
          )}
          {tieTyped && (
            <div className="k-golden">
              <div className="muted small">★ {t().goldenAsk}</div>
              <div className="chips" style={{ marginTop: 6 }}>
                {(['a', 'b'] as const).map((side) => (
                  <button key={side} className="chip" aria-pressed={golden === side} onClick={() => setGolden(side)}>
                    {side === 'a' ? `${match.team_a ?? ''}. ${names(match.a, meId)}` : `${match.team_b ?? ''}. ${names(match.b, meId)}`}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Пары микста до старта. Организатор выбирает партнёршу каждому мужчине;
 *  остальные видят, что уже назначено. */
function MixedPairs({
  game,
  pairs,
  editable,
  busy,
  onChange,
}: {
  game: Game
  pairs: [number, number][]
  editable: boolean
  busy: boolean
  onChange: (pairs: [number, number][]) => void
}) {
  const joined = game.slots.filter((s) => s.status === 'joined').map((s) => s.player)
  const men = joined.filter((p) => p.gender === 'm')
  const women = joined.filter((p) => p.gender === 'f')
  const partner = new Map(pairs)
  const taken = new Set(pairs.map(([, w]) => w))
  const name = (id: number) => joined.find((p) => p.id === id)?.first_name ?? '?'
  const withLevel = (p: (typeof joined)[number]) => `${p.first_name}${p.level != null ? ` · ${p.level.toFixed(2)}` : ''}`

  function pick(man: number, woman: number | null) {
    const next = pairs.filter(([m]) => m !== man)
    if (woman) next.push([man, woman])
    // Порядок пар — как мужчины стоят в составе: номера пар стабильны
    next.sort((a, b) => men.findIndex((p) => p.id === a[0]) - men.findIndex((p) => p.id === b[0]))
    onChange(next)
  }

  if (!editable) {
    return (
      <div style={{ marginTop: 12 }}>
        <div className="title">{t().pairsTitle}</div>
        {pairs.length === 0 ? (
          <div className="muted" style={{ marginTop: 4 }}>
            {t().pairsEmpty}
          </div>
        ) : (
          pairs.map(([m, w], i) => (
            <div key={m} style={{ padding: '4px 0' }}>
              {i + 1}. {name(m)} + {name(w)}
            </div>
          ))
        )}
      </div>
    )
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div className="row between">
        <div className="title">{t().pairsTitle}</div>
        <span className="muted small">{t().pairsSaved(pairs.length, Math.max(men.length, women.length))}</span>
      </div>
      <div className="muted small" style={{ margin: '4px 0 8px' }}>
        {t().pairsHint}
      </div>
      {men.map((man) => (
        <div key={man.id} className="pair-row">
          <span className="pair-man">{withLevel(man)}</span>
          <select
            aria-label={man.first_name}
            disabled={busy}
            value={partner.get(man.id) ?? ''}
            onChange={(e) => pick(man.id, e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">{t().pairNone}</option>
            {women
              .filter((w) => !taken.has(w.id) || partner.get(man.id) === w.id)
              .map((w) => (
                <option key={w.id} value={w.id}>
                  {withLevel(w)}
                </option>
              ))}
          </select>
        </div>
      ))}
    </div>
  )
}

/** «Король корта» до старта: организатор может сам составить пары, остальных
 *  приложение соберёт по уровню (сильный со слабым). Участники видят пары. */
function KingPairs({
  game,
  pairs,
  editable,
  busy,
  onChange,
}: {
  game: Game
  pairs: [number, number][]
  editable: boolean
  busy: boolean
  onChange: (pairs: [number, number][]) => void
}) {
  const joined = game.slots.filter((s) => s.status === 'joined').map((s) => s.player)
  const [first, setFirst] = useState<number | ''>('')
  const [second, setSecond] = useState<number | ''>('')
  const paired = new Set(pairs.flat())
  const free = joined.filter((p) => !paired.has(p.id))
  const name = (id: number) => joined.find((p) => p.id === id)?.first_name ?? '?'
  const label = (p: (typeof joined)[number]) => `${p.first_name}${p.level != null ? ` · ${p.level.toFixed(2)}` : ''}`

  if (!editable && pairs.length === 0) return null
  return (
    <div style={{ marginTop: 12 }}>
      <div className="title">{t().pairsTitle}</div>
      <div className="muted small" style={{ margin: '4px 0 8px' }}>
        {editable ? t().kingPairsHint : t().kingPairsRest}
      </div>
      {pairs.map(([x, y], i) => (
        <div key={`${x}-${y}`} className="row between" style={{ padding: '4px 0' }}>
          <span>
            {i + 1}. {name(x)} + {name(y)}
          </span>
          {editable && (
            <button
              className="ghost k-del"
              disabled={busy}
              aria-label={t().clearScore}
              onClick={() => onChange(pairs.filter((p) => p[0] !== x))}
            >
              ✕
            </button>
          )}
        </div>
      ))}
      {editable && free.length >= 2 && (
        <div className="pair-row k-add" style={{ marginTop: 6 }}>
          <select aria-label={t().pairNone} value={first} onChange={(e) => setFirst(e.target.value ? Number(e.target.value) : '')}>
            <option value="">—</option>
            {free.filter((p) => p.id !== second).map((p) => (
              <option key={p.id} value={p.id}>
                {label(p)}
              </option>
            ))}
          </select>
          <select aria-label={t().pairNone} value={second} onChange={(e) => setSecond(e.target.value ? Number(e.target.value) : '')}>
            <option value="">—</option>
            {free.filter((p) => p.id !== first).map((p) => (
              <option key={p.id} value={p.id}>
                {label(p)}
              </option>
            ))}
          </select>
          <button
            disabled={busy || first === '' || second === ''}
            onClick={() => {
              onChange([...pairs, [first as number, second as number]])
              setFirst('')
              setSecond('')
            }}
          >
            +
          </button>
        </div>
      )}
      {editable && free.length > 0 && pairs.length > 0 && (
        <div className="muted small" style={{ marginTop: 6 }}>
          {t().kingPairsAuto(free.length)}
        </div>
      )}
    </div>
  )
}

function mmss(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

/** Три коротких сигнала, когда время раунда вышло (у организатора: звук
 *  разрешён, потому что он сам нажал «Старт раунда»). */
function beep(ctx: AudioContext | null) {
  if (!ctx) return
  try {
    for (let i = 0; i < 3; i++) {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.frequency.value = 880
      gain.gain.value = 0.15
      osc.connect(gain).connect(ctx.destination)
      const at = ctx.currentTime + i * 0.35
      osc.start(at)
      osc.stop(at + 0.2)
    }
  } catch {
    /* без звука — переживём */
  }
}

/** Таймер раунда «Короля корта»: организатор запускает, у всех идёт отсчёт.
 *  Время берём с сервера (server_now), чтобы не зависеть от часов телефона. */
function RoundTimer({
  data,
  busy,
  onAction,
}: {
  data: Tournament
  busy: boolean
  onAction: (action: 'start' | 'stop') => void
}) {
  const [now, setNow] = useState(() => Date.now())
  const offset = useRef(0)
  const audio = useRef<AudioContext | null>(null)
  const rang = useRef<string | null>(null)
  useEffect(() => {
    if (data.server_now) offset.current = new Date(data.server_now).getTime() - Date.now()
  }, [data.server_now])
  const round = data.rounds_total
  const active = data.timer_started_at != null && data.timer_round === round
  useEffect(() => {
    if (!active) return
    const id = window.setInterval(() => setNow(Date.now()), 500)
    return () => window.clearInterval(id)
  }, [active])

  const minutes = data.round_minutes ?? 12
  const left = active ? new Date(data.timer_started_at!).getTime() + minutes * 60000 - (now + offset.current) : minutes * 60000
  const over = active && left <= 0
  useEffect(() => {
    if (over && rang.current !== data.timer_started_at) {
      rang.current = data.timer_started_at
      haptic('heavy')
      if (data.can_manage) beep(audio.current)
    }
  }, [over, data.timer_started_at, data.can_manage])

  function start() {
    if (data.can_manage && !audio.current) {
      try {
        const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
        audio.current = Ctx ? new Ctx() : null
      } catch {
        audio.current = null
      }
    }
    onAction('start')
  }

  const share = active ? Math.min(1, Math.max(0, 1 - left / (minutes * 60000))) : 0
  return (
    <div className={`k-timer${over ? ' over' : active ? ' run' : ''}`}>
      <div className="row between">
        <span className="muted small">
          ⏱ {t().roundN(round)} · {minutes} {t().minutes}
        </span>
        {active && !over && <span className="muted small">{t().timerRunning}</span>}
      </div>
      <div className="k-clock">{over ? `⏰ ${t().timerOver}` : mmss(left)}</div>
      <div className="k-bar">
        <div style={{ width: `${share * 100}%` }} />
      </div>
      {!active && !data.can_manage && <div className="muted small">{t().timerWaiting}</div>}
      {data.can_manage && (
        <div className="row" style={{ gap: 8, marginTop: 8 }}>
          <button style={{ flex: 1 }} className={active ? 'ghost' : undefined} disabled={busy} onClick={start}>
            {active ? `↺ ${t().timerRestart}` : `▶ ${t().timerStart(round)}`}
          </button>
          {active && (
            <button className="ghost" style={{ flex: 1 }} disabled={busy} onClick={() => onAction('stop')}>
              ⏹ {t().timerStop}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/** «Король корта»: куда уходят пары после раунда — та же логика, что на сервере
 *  (king_next_order): победитель вверх (на корте 1 — 👑 остаётся), проигравший
 *  вниз (на нижнем — остаётся или уходит отдыхать, если есть отдыхающая пара). */
function kingMoves(list: TMatch[], someoneRests: boolean): Map<number, { a: string; b: string }> {
  const out = new Map<number, { a: string; b: string }>()
  const last = Math.max(...list.map((m) => m.court))
  for (const m of list) {
    if (m.score_a == null || m.score_b == null) continue
    const tie = m.score_a === m.score_b
    if (tie && !m.golden) continue
    const aWon = m.score_a > m.score_b || (tie && m.golden === 'a')
    const up = m.court === 1 ? '👑' : '▲'
    const down = m.court === last ? (someoneRests ? '☕' : '=') : '▼'
    out.set(m.id, aWon ? { a: up, b: down } : { a: down, b: up })
  }
  return out
}

/** Строка таблицы для отрисовки: общая для «сейчас/итог» и «после раунда N». */
interface StatRow {
  key: number
  place: number
  move: number
  players: TPlayer[]
  wins: number
  draws: number
  losses: number
  points_for: number
  points_against: number
  crowns?: number
}

function teamName(players: TPlayer[]): string {
  return players.map((p) => p.first_name).join(' + ')
}

/** Пьедестал: 2 — 1 — 3. */
/** Итоги завершённого турнира файлом: бот присылает картинку и PDF в личку,
 *  или файл открывается здесь же (в приложении — системный просмотрщик со
 *  своим «Поделиться»). Ссылки берём заранее: открывать файл нужно прямо
 *  по нажатию, иначе браузер и Telegram его заблокируют. */
function ResultsShare({ gameId, me }: { gameId: number; me: PlayerMe }) {
  const [links, setLinks] = useState<ReportLinks | null>(null)
  const [sending, setSending] = useState(false)
  useEffect(() => {
    let alive = true
    api
      .reportLinks(gameId)
      .then((l) => alive && setLinks(l))
      .catch(() => alive && setLinks(null))
    return () => {
      alive = false
    }
  }, [gameId])

  async function send() {
    setSending(true)
    haptic('light')
    try {
      await api.reportSend(gameId)
      notify(t().resultsSent)
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setSending(false)
    }
  }

  const viaBot = Boolean(me.telegram_id)
  return (
    <div className="t-results">
      <div className="title">📊 {t().resultsTitle}</div>
      <div className="muted small" style={{ marginTop: 2 }}>
        {viaBot ? t().resultsHintBot : t().resultsHintFiles}
      </div>
      {viaBot && (
        <button style={{ width: '100%', marginTop: 10 }} disabled={sending} onClick={send}>
          {t().resultsToTelegram}
        </button>
      )}
      <div className="t-results-files">
        <button className="ghost" disabled={!links} onClick={() => links && openFile(links.png.url, links.png.name)}>
          🖼 {t().resultsImage}
        </button>
        <button className="ghost" disabled={!links} onClick={() => links && openFile(links.pdf.url, links.pdf.name)}>
          📄 {t().resultsPdf}
        </button>
      </div>
    </div>
  )
}

function Podium({ rows, pointsMode, unit, king = false }: { rows: StatRow[]; pointsMode: boolean; unit: string; king?: boolean }) {
  const at = (place: number) => rows.find((r) => r.place === place)
  const order = [2, 1, 3]
  if (!at(1)) return null
  return (
    <>
      <div className="title" style={{ marginTop: 14 }}>
        {t().podiumTitle}
      </div>
      <div className="t-podium">
        {order.map((place) => {
          const row = at(place)
          if (!row) return <div key={place} style={{ visibility: 'hidden' }} />
          return (
            <div key={place} className={`p${place}`}>
              <div className="medal">{MEDAL[place]}</div>
              <div className="who">{teamName(row.players)}</div>
              <div className="muted small pts">
                {king
                  ? `${row.wins}-${row.losses}${row.crowns ? ` · 👑${row.crowns}` : ''}`
                  : pointsMode
                    ? `${row.points_for} ${unit}`
                    : `${row.wins}-${row.draws}-${row.losses}`}
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

function StatsTable({
  rows,
  kind,
  meId,
  pointsMode,
  medals,
  showDeltas,
  finished,
}: {
  rows: StatRow[]
  kind: string
  meId: number
  pointsMode: boolean
  medals: boolean
  showDeltas: boolean
  finished: boolean
}) {
  return (
    <div className="t-stats" role="table">
      <div className="t-srow t-head muted" role="row">
        <span>#</span>
        <span>{isPairsKind(kind) ? t().colPair : t().colPlayer}</span>
        <span>{kind === 'king' ? t().colWL : t().colWDL}</span>
        <span>{t().colDelta}</span>
        <span>{pointsMode ? t().colPoints : t().gamesUnit}</span>
      </div>
      {rows.map((row) => {
        const mine = row.players.some((p) => p.id === meId)
        const diff = row.points_for - row.points_against
        // У пары с прогнозом уровня имена — в плашках, строку с именами не дублируем
        const pairBadges = isPairsKind(kind) && showDeltas && row.players.some((p) => p.delta != null)
        return (
          <div key={row.key} className={`t-srow${mine ? ' mine' : ''}`} role="row">
            <span className="t-place">
              {medals && MEDAL[row.place] ? MEDAL[row.place] : row.place}
              {row.move !== 0 && (
                <span className={`t-move ${row.move > 0 ? 'up' : 'down'}`}>
                  {row.move > 0 ? '▲' : '▼'}
                  {Math.abs(row.move)}
                </span>
              )}
            </span>
            <span className="t-who">
              {pairBadges ? (
                <span className="muted small">{t().teamN(row.key)}</span>
              ) : (
                <span className={`t-names${isPairsKind(kind) ? ' wrap' : ''}`}>
                  {isPairsKind(kind) ? `${row.key}. ` : ''}
                  {teamName(row.players)}
                </span>
              )}
              {kind === 'king' && (row.crowns ?? 0) > 0 && (
                <span className="muted small" title={t().crownsHint}>
                  {' '}
                  👑{row.crowns}
                </span>
              )}
              {showDeltas && row.players.some((p) => p.delta != null) && (
                <span className="t-badges">
                  {row.players.map((p) =>
                    p.delta == null ? null : (
                      <span key={p.id} className={p.delta > 0 ? 'badge' : p.delta < 0 ? 'badge warn' : 'badge neutral'}>
                        {isPairsKind(kind) ? `${p.first_name} ` : ''}
                        {finished ? '' : '≈'}
                        {fmtDelta(p.delta)}
                      </span>
                    ),
                  )}
                </span>
              )}
            </span>
            <span style={{ fontWeight: pointsMode ? 400 : 700 }}>
              {kind === 'king' ? `${row.wins}-${row.losses}` : `${row.wins}-${row.draws}-${row.losses}`}
            </span>
            <span className={`t-diff ${diff > 0 ? 'pos' : diff < 0 ? 'neg' : ''}`}>
              {diff > 0 ? '+' : diff < 0 ? '−' : ''}
              {Math.abs(diff)}
            </span>
            <span>
              <b style={{ fontWeight: pointsMode ? 700 : 400 }}>{row.points_for}</b>
              <span className="muted">-{row.points_against}</span>
            </span>
          </div>
        )
      })}
    </div>
  )
}

export default function TournamentView({ game, me, onGameChanged }: Props) {
  const [data, setData] = useState<Tournament | null>(null)
  const [busy, setBusy] = useState(false)
  const [openRound, setOpenRound] = useState<number | null>(null)
  const [tab, setTab] = useState<'score' | 'stats' | null>(null)
  // 0 — текущая таблица (или итог), N — после раунда N
  const [statsRound, setStatsRound] = useState(0)
  const [editing, setEditing] = useState(false)
  const busyRef = useRef(false)
  const lastStatus = useRef<string | null>(null)

  const load = useCallback(async () => {
    try {
      setData(await api.tournament(game.id))
    } catch {
      /* сеть моргнула — следующий опрос перечитает */
    }
  }, [game.id])

  // Перечитываем при изменении состава: от него зависит, можно ли стартовать
  // …и при смене пола участника в миксте: от него зависят пары и готовность к старту
  const roster = game.slots.map((s) => `${s.player.id}${s.player.gender ?? ''}${s.status}`).join(',')
  useEffect(() => {
    void load()
  }, [load, game.joined_count, game.status, roster])

  // Онлайн: пока турнир идёт, счёт и таблица обновляются сами
  useEffect(() => {
    if (data?.status !== 'running') return
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible' && !busyRef.current) void load()
    }, 15000)
    return () => window.clearInterval(id)
  }, [data?.status, load])

  // Организатор завершил турнир на своём телефоне — экран игры тоже обновим
  useEffect(() => {
    if (!data) return
    if (lastStatus.current && lastStatus.current !== data.status) {
      if (data.status === 'finished') setTab(null)
      onGameChanged()
    }
    lastStatus.current = data.status
  }, [data, onGameChanged])

  const rounds = useMemo(() => {
    const map = new Map<number, TMatch[]>()
    for (const m of data?.matches ?? []) map.set(m.round, [...(map.get(m.round) ?? []), m])
    return [...map.entries()]
  }, [data])

  const people = useMemo(() => {
    const map = new Map<number, TPlayer>()
    for (const m of data?.matches ?? []) for (const p of [...m.a, ...m.b]) map.set(p.id, p)
    for (const r of data?.standings ?? []) for (const p of r.players) map.set(p.id, p)
    return map
  }, [data])

  async function act(fn: () => Promise<Tournament>, gameChanged = false) {
    setBusy(true)
    busyRef.current = true
    haptic('medium')
    try {
      const next = await fn()
      // Завершили турнир — сразу показываем итог
      if (next.status === 'finished' && lastStatus.current !== 'finished') setTab(null)
      lastStatus.current = next.status
      setData(next)
      if (gameChanged) onGameChanged()
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setBusy(false)
      busyRef.current = false
    }
  }

  if (!data) return null

  const running = data.status === 'running'
  const finished = data.status === 'finished'
  const registration = !running && !finished
  const shownRound = openRound ?? data.current_round ?? data.rounds_total
  const pointsMode = data.score_mode === 'points'
  const unit = pointsMode ? t().pointsUnit : t().gamesUnit
  const activeTab = tab ?? (finished ? 'stats' : 'score')

  // Таблица для вкладки «Статистика»
  const byKey = new Map(data.standings.map((r) => [r.key, r.players]))
  const lastTable = data.round_tables[data.round_tables.length - 1]
  const lastMove = new Map((lastTable?.rows ?? []).map((r) => [r.key, r.move]))
  const picked = statsRound ? data.round_tables.find((rt) => rt.round === statsRound) : undefined
  const statRows: StatRow[] = picked
    ? picked.rows.map((r) => ({ ...r, players: byKey.get(r.key) ?? [] }))
    : data.standings.map((r) => ({ ...r, move: lastMove.get(r.key) ?? 0 }))
  const myRow = data.standings.find((r) => r.players.some((p) => p.id === me.id))
  const myDelta = myRow?.players.find((p) => p.id === me.id)?.delta
  const totalPoints = data.matches.reduce((sum, m) => sum + (m.score_a ?? 0) + (m.score_b ?? 0), 0)
  const king = data.kind === 'king'
  const restingNow = (data.resting[String(shownRound)] ?? []).map((id) => people.get(id)?.first_name ?? '?')
  const iRest = (data.resting[String(shownRound)] ?? []).includes(me.id)

  return (
    <div className="card">
      <div className="row between">
        <div className="title">{t().tournamentTitle}</div>
        <span className={finished ? 'badge' : 'badge neutral'}>
          {finished ? t().statusFinished : running ? t().statusRunning : t().registrationOpen}
        </span>
      </div>
      <div className="muted" style={{ marginTop: 4 }}>
        {data.kind === 'americano'
          ? t().kindHintAmericano
          : data.kind === 'mexicano'
            ? t().kindHintMexicano
            : king
              ? t().kindHintKing
              : data.mixed
                ? t().mixedHint
                : t().kindHintPairs}
        {king
          ? ` · ${t().roundMinutesShort(data.round_minutes ?? 12)}`
          : data.points_per_match
            ? ` · ${t().matchTo} ${data.points_per_match} ${unit}`
            : ''}
      </div>
      <div className="muted small" style={{ marginTop: 4 }}>
        {king ? t().rankHintKing : pointsMode ? t().rankHintPoints : t().rankHintGames}
      </div>

      {registration && data.plan && (
        <div className="muted small" style={{ marginTop: 8 }}>
          {data.plan.rounds
            ? t().planLine(data.plan.players, data.plan.rounds, data.plan.courts, data.plan.per_player)
            : t().planUnlimited(data.plan.players, data.plan.courts)}
          {data.plan.resting > 0 && <> · {king ? t().kingRestHint : t().restHint(data.plan.resting)}</>}
        </div>
      )}

      {data.can_manage && !finished && (
        <button className="ghost" style={{ width: '100%', marginTop: 12 }} onClick={() => setEditing(true)}>
          {t().editTournament}
        </button>
      )}
      {editing && (
        <TournamentEditor
          game={game}
          data={data}
          onClose={(changed) => {
            setEditing(false)
            if (changed) {
              void load()
              onGameChanged()
            }
          }}
        />
      )}

      {registration && king && (
        <KingPairs
          game={game}
          pairs={data.draft_pairs}
          editable={data.can_manage}
          busy={busy}
          onChange={(pairs) => act(() => api.setPairs(game.id, pairs))}
        />
      )}

      {registration && data.mixed && (
        <MixedPairs
          game={game}
          pairs={data.draft_pairs}
          editable={data.can_manage}
          busy={busy}
          onChange={(pairs) => act(() => api.setPairs(game.id, pairs))}
        />
      )}

      {registration && data.can_manage && (
        <>
          {data.start_problem && (
            <div className="muted" style={{ marginTop: 10 }}>
              {data.start_problem}
            </div>
          )}
          <button
            style={{ width: '100%', marginTop: 12 }}
            disabled={busy || !data.can_start}
            onClick={async () => {
              if (await confirmAction(t().startConfirm)) await act(() => api.tournamentAction(game.id, 'start'), true)
            }}
          >
            {t().startTournament}
          </button>
        </>
      )}

      {(running || finished) && (
        <>
          <div className="row between" style={{ marginTop: 12 }}>
            <span className="muted">
              {isRoundByRound(data.kind)
                ? `${t().roundOf(data.rounds_total, data.rounds_planned)} · ${t().progress(data.scored, data.total_matches)}`
                : t().progress(data.scored, data.total_matches)}
            </span>
            {running && <span className="muted small">● {t().liveHint}</span>}
          </div>
          <div className="t-progress">
            <div style={{ width: `${data.total_matches ? (100 * data.scored) / data.total_matches : 0}%` }} />
          </div>

          {finished && (data.can_manage || myRow) && <ResultsShare gameId={game.id} me={me} />}

          {running && king && (
            <RoundTimer data={data} busy={busy} onAction={(action) => act(() => api.tournamentTimer(game.id, action))} />
          )}

          <div className="seg full t-tabs" role="tablist">
            <button role="tab" aria-pressed={activeTab === 'score'} onClick={() => setTab('score')}>
              {t().tabScore}
            </button>
            <button role="tab" aria-pressed={activeTab === 'stats'} onClick={() => setTab('stats')}>
              {t().tabStats}
            </button>
          </div>

          {activeTab === 'score' && (
            <>
              <div className="chips scroll" style={{ marginTop: 12 }}>
                {rounds.map(([round, list]) => {
                  const done = list.every((m) => m.score_a != null)
                  return (
                    <button
                      key={round}
                      className="chip"
                      aria-pressed={shownRound === round}
                      onClick={() => setOpenRound(round)}
                    >
                      {t().roundN(round)}
                      {done ? ' ✓' : ''}
                    </button>
                  )
                })}
              </div>
              {running && data.can_manage && (
                <div className="muted small" style={{ marginTop: 6 }}>
                  {t().tapToScore}
                </div>
              )}
              {data.can_correct && (
                <div className="muted small" style={{ marginTop: 6 }}>
                  {t().correctHint}
                </div>
              )}

              {rounds
                .filter(([round]) => round === shownRound)
                .map(([round, list]) => {
                  const moves = king ? kingMoves(list, (data.resting[String(round)] ?? []).length > 0) : null
                  return (
                    <div key={round}>
                      {list.map((m) => (
                        <MatchLine
                          key={`${m.id}-${m.score_a}-${m.score_b}-${m.golden}`}
                          match={m}
                          meId={me.id}
                          // В «Короле корта» счёт прошлого раунда меняется, только пока следующий не построен
                          editable={
                            (running && data.can_manage && (!king || round === data.rounds_total)) ||
                            (data.can_correct && m.score_a != null)
                          }
                          correcting={finished}
                          total={pointsMode ? data.points_per_match : null}
                          busy={busy}
                          king={king}
                          moves={moves?.get(m.id)}
                          onSave={(a, b, golden) => act(() => api.setTournamentScore(m.id, a, b, golden))}
                        />
                      ))}
                    </div>
                  )
                })}
              {king && running && shownRound === data.rounds_total && (
                <div className="muted small" style={{ marginTop: 6 }}>
                  {t().kingMovesHint}
                </div>
              )}
              {restingNow.length > 0 && (
                <div className={`t-rest small${iRest ? ' mine' : ' muted'}`}>
                  {iRest ? `☕ ${t().youRest}. ` : '☕ '}
                  {t().restingNow(restingNow.join(', '))}
                </div>
              )}
            </>
          )}

          {activeTab === 'stats' && (
            <>
              {data.standings.length === 0 || data.scored === 0 ? (
                <div className="muted" style={{ marginTop: 12 }}>
                  {t().tapToScore}
                </div>
              ) : (
                <>
                  {myRow && (
                    <div className="t-me">
                      <div className="row between">
                        <b>{t().myResultTitle}</b>
                        <b>{t().myPlace(myRow.place, data.standings.length)}</b>
                      </div>
                      <div className="muted small" style={{ marginTop: 4 }}>
                        {t().myLine(myRow.wins, myRow.draws, myRow.losses, myRow.points_for, myRow.points_against, unit)}
                      </div>
                      {data.rated && myDelta != null && (
                        <div className="small" style={{ marginTop: 4 }}>
                          {t().myLevel}:{' '}
                          <span className={myDelta > 0 ? 'badge' : myDelta < 0 ? 'badge warn' : 'badge neutral'}>
                            {finished ? '' : '≈'}
                            {fmtDelta(myDelta)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {finished && !statsRound && <Podium rows={statRows} pointsMode={pointsMode} unit={unit} king={king} />}

                  <div className="chips scroll" style={{ marginTop: 12 }}>
                    <button className="chip" aria-pressed={statsRound === 0} onClick={() => setStatsRound(0)}>
                      {finished ? t().statsFinal : t().statsLive}
                    </button>
                    {[...data.round_tables].reverse().map((rt) => (
                      <button
                        key={rt.round}
                        className="chip"
                        aria-pressed={statsRound === rt.round}
                        onClick={() => setStatsRound(rt.round)}
                      >
                        {t().roundShort(rt.round)}
                        {rt.complete ? '' : '…'}
                      </button>
                    ))}
                  </div>
                  <div className="muted small" style={{ marginTop: 6 }}>
                    {picked
                      ? `${t().afterRound(picked.round)}${picked.complete ? '' : ` · ${t().roundNotDone}`}`
                      : finished
                        ? t().totalsLine(data.scored, totalPoints, unit)
                        : t().liveHint}
                  </div>

                  <StatsTable
                    rows={statRows}
                    kind={data.kind}
                    meId={me.id}
                    pointsMode={pointsMode}
                    medals={finished && !picked}
                    showDeltas={data.rated && !picked}
                    finished={finished}
                  />
                  {data.rated && running && !picked && (
                    <div className="muted small" style={{ marginTop: 6 }}>
                      {t().forecastHint}
                    </div>
                  )}
                  {data.my_why && !picked && (
                    <Why why={data.my_why} tournament={pointsMode ? 'americano' : 'pairs'} />
                  )}
                </>
              )}
            </>
          )}

          {running && data.can_manage && (
            <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
              {isRoundByRound(data.kind) && data.can_next_round && (
                <>
                  <button
                    disabled={busy}
                    onClick={() => {
                      setOpenRound(null)
                      setTab('score')
                      void act(() => api.tournamentAction(game.id, 'next-round'))
                    }}
                  >
                    {t().nextRound(data.rounds_total + 1)}
                  </button>
                  <div className="muted small">{king ? t().kingNextHint : t().nextRoundHint}</div>
                </>
              )}
              {isRoundByRound(data.kind) && !data.can_next_round && data.next_round_problem && data.current_round == null && (
                <div className="muted small">{data.next_round_problem}</div>
              )}
              {data.can_undo_round && (
                <button
                  className="ghost"
                  disabled={busy}
                  onClick={async () => {
                    if (await confirmAction(t().undoRoundConfirm)) {
                      setOpenRound(null)
                      await act(() => api.tournamentAction(game.id, 'undo-round'))
                    }
                  }}
                >
                  {t().undoRound(data.rounds_total)}
                </button>
              )}
              <button
                className={
                  // В mexicano главное действие — счёт и следующий раунд; «Завершить» выделяем,
                  // только когда все запланированные раунды сыграны
                  isRoundByRound(data.kind) &&
                  !(data.rounds_planned != null && data.rounds_total >= data.rounds_planned && data.current_round == null)
                    ? 'ghost'
                    : undefined
                }
                disabled={busy || data.scored === 0}
                onClick={async () => {
                  if (await confirmAction(t().finishConfirm(data.total_matches - data.scored)))
                    await act(() => api.tournamentAction(game.id, 'finish'), true)
                }}
              >
                {t().finishTournament}
              </button>
              {data.scored === 0 && (
                <button
                  className="ghost"
                  disabled={busy}
                  onClick={async () => {
                    if (await confirmAction(t().resetConfirm))
                      await act(() => api.tournamentAction(game.id, 'reset'), true)
                  }}
                >
                  {t().resetTournament}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
